import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, IconButton, Paper } from '@mui/material';
import { CloseRounded, PhoneRounded, MailRounded } from '@mui/icons-material';
import { BRAND } from '../theme';
import { COMPANY, SOCIAL } from './brand';

const WHATSAPP = SOCIAL.find(s => s.label === 'WhatsApp');
const WHATSAPP_HREF = `${COMPANY.whatsapp}?text=${encodeURIComponent('Hi AnAInfo, I found you on anainfo.ai and would like to know more.')}`;

const ChatIcon = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
    <path fill="currentColor" d="M4 4h16a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H9l-5 4v-4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2zm3 6.5a1.5 1.5 0 1 0 0 .01zm5 0a1.5 1.5 0 1 0 0 .01zm5 0a1.5 1.5 0 1 0 0 .01z" />
  </svg>
);

const Option = ({ href, icon, title, text, external }) => (
  <Box component="a" href={href} {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})} sx={{
    display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, borderRadius: 3, textDecoration: 'none', color: 'text.primary',
    border: `1px solid ${BRAND.line}`, '&:hover': { borderColor: 'primary.main', bgcolor: '#f7f6ff' },
  }}>
    {icon}
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontWeight: 600, fontSize: 14.5 }}>{title}</Typography>
      <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>{text}</Typography>
    </Box>
  </Box>
);

// Floating "Chat with us" button, fixed bottom-right. It opens a small panel
// with the same contact routes as anainfo.com: WhatsApp chat, phone and email.
const ChatButton = () => {
  const [open, setOpen] = useState(false);
  const launcher = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') { setOpen(false); launcher.current?.focus(); } };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    <>
      {open && (
        <Paper id="chat-panel" role="dialog" aria-label="Chat with AnAInfo" elevation={0} sx={{
          position: 'fixed', zIndex: 1300, right: { xs: 12, sm: 24 }, bottom: { xs: 80, sm: 92 },
          width: { xs: 'calc(100vw - 24px)', sm: 340 }, borderRadius: '20px', overflow: 'hidden',
          boxShadow: '0 24px 60px -12px rgba(20,17,43,0.45)', border: `1px solid ${BRAND.line}`,
        }}>
          <Box sx={{ background: BRAND.heroGradient, color: '#fff', px: 2.5, py: 2, display: 'flex', alignItems: 'center' }}>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontWeight: 700 }}>Chat with AnAInfo</Typography>
              <Typography sx={{ fontSize: 12.5, opacity: 0.75 }}>We usually reply within a few minutes</Typography>
            </Box>
            <IconButton aria-label="Close chat" onClick={() => setOpen(false)} sx={{ color: '#fff' }}><CloseRounded /></IconButton>
          </Box>
          <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.25, bgcolor: '#fff' }}>
            <Option href={WHATSAPP_HREF} external title="Chat on WhatsApp" text={COMPANY.phone}
              icon={<Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: WHATSAPP.color, color: '#fff', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
                <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true"><path fill="currentColor" d={WHATSAPP.d} /></svg>
              </Box>} />
            <Option href={COMPANY.phoneHref} title="Call us" text={COMPANY.phone}
              icon={<Box sx={{ width: 40, height: 40, borderRadius: '50%', background: BRAND.gradient, color: '#fff', display: 'grid', placeItems: 'center', flexShrink: 0 }}><PhoneRounded fontSize="small" /></Box>} />
            <Option href={`mailto:${COMPANY.email}`} title="Email us" text={COMPANY.email}
              icon={<Box sx={{ width: 40, height: 40, borderRadius: '50%', background: BRAND.gradient, color: '#fff', display: 'grid', placeItems: 'center', flexShrink: 0 }}><MailRounded fontSize="small" /></Box>} />
          </Box>
        </Paper>
      )}
      <Box component="button" type="button" ref={launcher} onClick={() => setOpen(o => !o)}
        aria-controls="chat-panel" aria-expanded={open} aria-label={open ? 'Close chat' : 'Chat with us'}
        sx={{
          position: 'fixed', zIndex: 1300, right: { xs: 16, sm: 24 }, bottom: { xs: 16, sm: 24 },
          display: 'inline-flex', alignItems: 'center', gap: 1, border: 0, cursor: 'pointer',
          px: open ? 1.75 : 2.5, py: 1.6, borderRadius: 999, color: '#fff', background: BRAND.gradient,
          font: '600 15px "Poppins", sans-serif', boxShadow: '0 10px 30px -8px rgba(61,123,255,0.75)',
          transition: 'transform .2s', '&:hover': { transform: 'translateY(-2px)' },
          '@media (prefers-reduced-motion: reduce)': { '&:hover': { transform: 'none' } },
          '@media print': { display: 'none' },
        }}>
        {open ? <CloseRounded /> : <><ChatIcon /><span>Chat with us</span></>}
      </Box>
    </>
  );
};

export default ChatButton;
